import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

const ACCOUNT_TYPES = ['Distributor', 'Retail Store', 'Dealer', 'Sub-Distributor'];
const REGIONS = ['NCR', 'Luzon', 'Visayas', 'Mindanao'];
const AREAS = ['Metro Manila', 'Cebu', 'Davao', 'Iloilo', 'Cagayan de Oro', 'Batangas', 'Pampanga'];
const SALESPERSONS = ['Juan dela Cruz', 'Maria Santos', 'Pedro Reyes', 'Ana Gomez', 'Carlo Bautista'];
const PAYMENT_TERMS = ['COD', 'Net 7', 'Net 15', 'Net 30', 'Net 60'];

const EMPTY_FORM = {
  name: '', type: 'Retail Store', region: 'NCR', area: 'Metro Manila',
  salesperson: 'Juan dela Cruz', address: '', city: '', province: '',
  zipCode: '', contactPerson: '', contactPhone: '', contactEmail: '',
  lat: '', lng: '', creditLimit: '', paymentTerms: 'Net 30', status: 'Active', notes: '',
};

export default function AccountFormModal({ isOpen, onClose, onSave, editAccount }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editAccount) {
      setForm({
        name: editAccount?.name || '',
        type: editAccount?.type || 'Retail Store',
        region: editAccount?.region || 'NCR',
        area: editAccount?.area || 'Metro Manila',
        salesperson: editAccount?.salesperson || 'Juan dela Cruz',
        address: editAccount?.address || '',
        city: editAccount?.city || '',
        province: editAccount?.province || '',
        zipCode: editAccount?.zipCode || '',
        contactPerson: editAccount?.contactPerson || '',
        contactPhone: editAccount?.contactPhone || '',
        contactEmail: editAccount?.contactEmail || '',
        lat: editAccount?.lat || '',
        lng: editAccount?.lng || '',
        creditLimit: editAccount?.creditLimit || '',
        paymentTerms: editAccount?.paymentTerms || 'Net 30',
        status: editAccount?.status || 'Active',
        notes: editAccount?.notes || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [editAccount, isOpen]);

  const validate = () => {
    const e = {};
    if (!form?.name?.trim()) e.name = 'Account name is required';
    if (!form?.address?.trim()) e.address = 'Address is required';
    if (!form?.contactPhone?.trim()) e.contactPhone = 'Contact phone is required';
    if (form?.creditLimit && isNaN(Number(form?.creditLimit))) e.creditLimit = 'Must be a valid number';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e)?.length > 0) { setErrors(e); return; }
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setSaving(false);
    onSave({ ...form, id: editAccount?.id || Date.now(), creditLimit: Number(form?.creditLimit) || 0 });
    onClose();
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name={editAccount ? 'Pencil' : 'UserPlus'} size={18} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h2 className="font-heading text-base font-semibold text-foreground">
                {editAccount ? 'Edit Account' : 'Add New Account'}
              </h2>
              <p className="font-caption text-xs text-muted-foreground">Fill in the account details below</p>
            </div>
          </div>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors"
            onClick={onClose}
          >
            <Icon name="X" size={18} color="currentColor" strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5 custom-scrollbar">
          {/* Basic Info */}
          <section>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Info" size={14} color="var(--color-primary)" strokeWidth={2} />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Input label="Account Name" type="text" placeholder="e.g. Santos General Merchandise" value={form?.name} onChange={e => set('name', e?.target?.value)} error={errors?.name} required />
              </div>
              <div>
                <label className="font-caption text-sm font-medium text-foreground block mb-1">Account Type <span className="text-destructive">*</span></label>
                <select className="w-full h-10 pl-3 pr-8 text-sm border border-border rounded-md bg-background text-foreground font-caption appearance-none focus:outline-none focus:ring-2 focus:ring-ring" value={form?.type} onChange={e => set('type', e?.target?.value)}>
                  {ACCOUNT_TYPES?.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="font-caption text-sm font-medium text-foreground block mb-1">Status</label>
                <select className="w-full h-10 pl-3 pr-8 text-sm border border-border rounded-md bg-background text-foreground font-caption appearance-none focus:outline-none focus:ring-2 focus:ring-ring" value={form?.status} onChange={e => set('status', e?.target?.value)}>
                  {['Active', 'Inactive', 'On Hold']?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Address */}
          <section>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="MapPin" size={14} color="var(--color-primary)" strokeWidth={2} />
              Philippine Address
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Input label="Street Address" type="text" placeholder="House/Unit No., Street Name, Barangay" value={form?.address} onChange={e => set('address', e?.target?.value)} error={errors?.address} required />
              </div>
              <Input label="City / Municipality" type="text" placeholder="e.g. Quezon City" value={form?.city} onChange={e => set('city', e?.target?.value)} />
              <Input label="Province" type="text" placeholder="e.g. Metro Manila" value={form?.province} onChange={e => set('province', e?.target?.value)} />
              <Input label="ZIP Code" type="text" placeholder="e.g. 1100" value={form?.zipCode} onChange={e => set('zipCode', e?.target?.value)} />
              <div>
                <label className="font-caption text-sm font-medium text-foreground block mb-1">Region</label>
                <select className="w-full h-10 pl-3 pr-8 text-sm border border-border rounded-md bg-background text-foreground font-caption appearance-none focus:outline-none focus:ring-2 focus:ring-ring" value={form?.region} onChange={e => set('region', e?.target?.value)}>
                  {REGIONS?.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="font-caption text-sm font-medium text-foreground block mb-1">Area</label>
                <select className="w-full h-10 pl-3 pr-8 text-sm border border-border rounded-md bg-background text-foreground font-caption appearance-none focus:outline-none focus:ring-2 focus:ring-ring" value={form?.area} onChange={e => set('area', e?.target?.value)}>
                  {AREAS?.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* GPS */}
          <section>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Navigation" size={14} color="var(--color-primary)" strokeWidth={2} />
              GPS Coordinates
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Latitude" type="text" placeholder="e.g. 14.5995" value={form?.lat} onChange={e => set('lat', e?.target?.value)} />
              <Input label="Longitude" type="text" placeholder="e.g. 120.9842" value={form?.lng} onChange={e => set('lng', e?.target?.value)} />
            </div>
          </section>

          {/* Contact */}
          <section>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Phone" size={14} color="var(--color-primary)" strokeWidth={2} />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Contact Person" type="text" placeholder="Full name" value={form?.contactPerson} onChange={e => set('contactPerson', e?.target?.value)} />
              <Input label="Phone Number" type="tel" placeholder="+63 9XX XXX XXXX" value={form?.contactPhone} onChange={e => set('contactPhone', e?.target?.value)} error={errors?.contactPhone} required />
              <div className="md:col-span-2">
                <Input label="Email Address" type="email" placeholder="account@example.com" value={form?.contactEmail} onChange={e => set('contactEmail', e?.target?.value)} />
              </div>
            </div>
          </section>

          {/* Assignment & Credit */}
          <section>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="CreditCard" size={14} color="var(--color-primary)" strokeWidth={2} />
              Assignment &amp; Credit Terms
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="font-caption text-sm font-medium text-foreground block mb-1">Assigned Salesperson</label>
                <select className="w-full h-10 pl-3 pr-8 text-sm border border-border rounded-md bg-background text-foreground font-caption appearance-none focus:outline-none focus:ring-2 focus:ring-ring" value={form?.salesperson} onChange={e => set('salesperson', e?.target?.value)}>
                  {SALESPERSONS?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <Input label="Credit Limit (₱)" type="number" placeholder="e.g. 50000" value={form?.creditLimit} onChange={e => set('creditLimit', e?.target?.value)} error={errors?.creditLimit} />
              <div>
                <label className="font-caption text-sm font-medium text-foreground block mb-1">Payment Terms</label>
                <select className="w-full h-10 pl-3 pr-8 text-sm border border-border rounded-md bg-background text-foreground font-caption appearance-none focus:outline-none focus:ring-2 focus:ring-ring" value={form?.paymentTerms} onChange={e => set('paymentTerms', e?.target?.value)}>
                  {PAYMENT_TERMS?.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Notes */}
          <section>
            <label className="font-caption text-sm font-medium text-foreground block mb-1">Notes / Remarks</label>
            <textarea
              className="w-full h-20 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground font-caption resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              placeholder="Additional notes about this account..."
              value={form?.notes}
              onChange={e => set('notes', e?.target?.value)}
            />
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="default" loading={saving} iconName="Save" iconPosition="left" onClick={handleSubmit}>
            {editAccount ? 'Save Changes' : 'Add Account'}
          </Button>
        </div>
      </div>
    </div>
  );
}